import { Box, Stack, Typography, useTheme } from '@mui/material';
import React from 'react';

import { colors } from '../../../components';
import { LinearProgressWrapper } from '../../../components/global-styles/GlobalStyles';
import CommonIconAvatar from '../../../components/ui-components/CommonIconAvatar';
import CommonStatusChip from '../../../components/ui-components/StatusChipComponent';
import { AssetApiResponse } from '../../../services/assets-service';
import { ContractApiResponse } from '../../../services/contract-services';
import { formatCurrency, formatToUSDate } from '../../../utils/helper';
import { useTranslation } from '../../../utils/translations';

interface LoanProgressCardProps {
    contract: ContractApiResponse;
    assetsData: AssetApiResponse[];
    isActive: boolean;
    paymentPercentage: number;
}

export const LoanProgressCard: React.FC<LoanProgressCardProps> = ({
    contract,
    assetsData,
    isActive,
    paymentPercentage,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack>
            <Typography variant="h6" component={'h2'} mb={1.5}>
                {t('contracts.details.loanProgress')}
            </Typography>
            <Stack
                sx={{
                    borderRadius: 3,
                    bgcolor: colors.fullWhite,
                    border: `1px solid ${colors.primaryLight[100]}`,
                    p: 2,
                }}
            >
                {/* Contract Header */}
                <Box display="flex" justifyContent="space-between" flexWrap={'wrap'}>
                    <Box display="flex" alignItems="start" gap={1.5}>
                        <CommonIconAvatar
                            icon="icon-contracts"
                            iconPadding={{ xs: 1.8, md: 2.25 }}
                            iconSize={24}
                            iconBgColor={colors.primaryLight[30]}
                            iconColor={colors.primaryLight[100]}
                        />
                        <Stack direction={'row'} gap={1.5} alignItems={'start'}>
                            <Box>
                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                    <Typography variant="subtitle1" component={'h3'}>
                                        {contract.AspireId}
                                    </Typography>
                                    <CommonStatusChip
                                        label={isActive ? t('contracts.status.active') : t('contracts.status.closed')}
                                        chipBgColor={isActive ? colors.successPreset[30] : colors.errorPreset[10]}
                                    />
                                    <CommonStatusChip
                                        label={
                                            contract.IsPrimary
                                                ? t('contracts.details.Primary')
                                                : t('contracts.details.Secondary')
                                        }
                                    />
                                    <CommonStatusChip
                                        label={
                                            contract.IsLoan ? t('contracts.details.Loan') : t('contracts.details.Lease')
                                        }
                                        chipBgColor={colors.bluePreset[30]}
                                    />
                                </Stack>
                                <Typography variant="caption" color="textSecondary">
                                    {assetsData[0]?.AssetDesc ?? ''}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Stack sx={{ textAlign: 'right' }} width={{ xs: '100%', sm: 'fit-content' }} justifyContent={'end'}>
                        <Typography variant="body2" color="textSecondary" mb={0.5}>
                            {contract.IsLoan
                                ? t('contracts.details.totalOverdue')
                                : t('contracts.details.totalLeaseValue')}
                        </Typography>
                        <Typography variant="h3" fontWeight={600}>
                            {formatCurrency(contract.PrincipleAmount)}
                        </Typography>
                    </Stack>
                </Box>
                {/* Payment Progress */}
                <Box mt={2.5}>
                    <Typography
                        id="contract-payment-progress-label"
                        variant="caption"
                        color="textSecondary"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        {t('contracts.details.paymentProgress')}
                        <Typography component={'span'} variant="caption" color={theme.palette.text.primary}>
                            {contract.PaymentsReceived} {t('contracts.list.of')} {contract.NumberOfPayments}{' '}
                            {t('contracts.list.paymentsMade')}{' '}
                        </Typography>
                    </Typography>
                    <LinearProgressWrapper
                        variant="determinate"
                        value={paymentPercentage}
                        aria-labelledby="contract-payment-progress-label"
                    />
                </Box>

                {/* Dates */}
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} mt={1} gap={1}>
                    <Typography variant="caption" color="textSecondary">
                        {t('contracts.details.startDate')}
                        <Typography
                            component={'span'}
                            variant="caption"
                            ml={1}
                            fontWeight={600}
                            color={theme.palette.text.primary}
                        >
                            {formatToUSDate(contract.BookingDate)}
                        </Typography>
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {contract.IsLoan ? t('contracts.details.maturityDate') : t('contracts.details.termEndDate')}
                        <Typography
                            component={'span'}
                            variant="caption"
                            ml={1}
                            fontWeight={600}
                            color={theme.palette.text.primary}
                        >
                            {formatToUSDate(contract.MaturityDate)}
                        </Typography>
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
};
